import React, { useEffect, useState } from 'react';

// --- Types ---
interface BenefitProps {
  Icon: React.ComponentType;
  title: string;
}

interface PlusBannerProps {
  isMobile: boolean;
}

// --- Icon Components ---
const NoLinesGraphic = () => (
  <svg
    className='size-6'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <polygon points='13 2 3 14 12 14 11 22 21 10 12 10 13 2'></polygon>
  </svg>
);
const InstantAccessGraphic = () => (
  <svg
    className='size-6'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4' />
  </svg>
);
const SupportGraphic = () => (
  <svg
    className='size-6'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <circle cx='12' cy='12' r='10' />
    <polyline points='12 6 12 12 16 14' />
  </svg>
);
const PassportGraphic = () => (
  <svg
    className='size-6'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <rect x='2' y='4' width='20' height='16' rx='2' />
    <circle cx='12' cy='12' r='4' />
  </svg>
);
const EnglishSupportGraphic = () => (
  <svg
    className='size-6'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z' />
    <text
      x='12'
      y='14'
      textAnchor='middle'
      fontSize='8'
      fill='currentColor'
      fontWeight='bold'
    >
      EN
    </text>
  </svg>
);
const PricingGraphic = () => (
  <svg
    className='size-6'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <circle cx='12' cy='12' r='10' />
    <path d='M15.5 8.5a2.5 2.5 0 1 1-5 0' />
    <path d='M12 15.5V13a2.5 2.5 0 0 0-5 0V15' />
  </svg>
);
const CloseIcon = () => (
  <svg
    className='size-5'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.5'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <line x1='18' y1='6' x2='6' y2='18' />
    <line x1='6' y1='6' x2='18' y2='18' />
  </svg>
);

const CarSilhouette = () => (
  <div
    className='pointer-events-none absolute bottom-0 right-0 h-full w-2/5 text-white opacity-20 md:w-[35%]'
    aria-hidden='true'
  >
    <svg
      width='100%'
      height='100%'
      preserveAspectRatio='xMaxYMid meet'
      viewBox='0 0 200 150'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path
        d='M105.479 179.771C105.479 190.476 112.972 194.814 122.209 189.459C131.451 184.109 138.938 171.09 138.938 160.385C138.938 149.679 131.446 145.341 122.209 150.697C112.972 156.047 105.479 169.065 105.479 179.771Z'
        fill='none'
      />
      <path
        d='M272.451 37.5005C264.979 27.6217 245.661 18.0362 226.348 8.18674C217.935 3.89775 206.343 -0.381457 189.102 0.0293469C170.657 0.464603 168.936 0.445041 158.441 0.366793C147.737 0.288545 134.361 2.60665 115.311 11.7715C100.624 18.8383 80.8144 31.3042 69.9004 43.4621C69.8127 43.5599 69.7298 43.6528 69.6421 43.7506C52.2644 63.1366 44.3141 74.38 39.4444 78.175C18.8447 93.4774 12.4396 99.5515 6.60968 109.606C2.70519 116.326 0.428796 123.872 0.0339599 135.917C-0.107401 140.314 0.146074 145.229 1.42807 150.017C10.1242 180.739 66.6442 200.017 83.2566 196.614C90.7292 195.083 99.05 190.711 101.2 189.532C99.8494 186.749 99.1426 183.439 99.1426 179.766C99.1426 166.84 107.883 151.65 119.04 145.19C122.643 143.102 126.221 142.045 129.681 142.045C134.346 142.045 138.48 144.011 141.322 147.581C143.91 150.833 145.275 155.259 145.275 160.385C145.275 161.505 145.207 162.644 145.08 163.793L223.253 117.91C222.127 115.289 221.542 112.242 221.542 108.897C221.542 95.9716 230.282 80.7816 241.439 74.3213C245.042 72.233 248.62 71.1767 252.081 71.1767C256.745 71.1767 260.879 73.1426 263.721 76.7127C266.309 79.9649 267.674 84.3908 267.674 89.5161C267.674 90.3182 267.64 91.1251 267.577 91.9418L267.738 91.8489C272.49 89.0955 275.741 83.6866 275.985 78.0478L276 49.6046C276 48.0592 275.551 41.6037 272.451 37.5054V37.5005ZM19.1372 140.118C19.1372 140.118 14.4528 137.169 10.6068 134.524C6.76567 131.878 5.52267 129.403 5.30331 128.919C3.89945 125.828 6.59506 118.263 7.45785 118.747C8.44738 119.304 7.40423 121.97 13.59 125.833C20.9846 130.445 19.1323 140.118 19.1323 140.118H19.1372ZM51.6453 173.662C51.6453 174.895 50.7777 175.399 49.7102 174.782L20.8189 158.047C20.3071 157.749 19.8879 157.025 19.8879 156.433V151.812C19.8879 150.579 20.7555 150.075 21.823 150.692L50.7143 167.427C51.2261 167.725 51.6453 168.449 51.6453 169.041V173.662ZM81.1703 171.315C81.1703 171.315 76.3445 171.828 68.6867 167.872L60.1319 163.143C62.3888 158.443 67.3267 157.093 72.2304 159.284C77.7289 161.744 81.8088 162.365 83.8659 162.517C84.7043 162.576 85.338 163.304 85.2649 164.145C85.0406 166.786 83.8025 171.124 81.1703 171.315ZM140.362 87.3105C131.943 99.4977 127.078 111.328 121.136 121.95C119.991 123.994 117.676 125.085 115.375 124.679C106.615 123.138 86.1082 117.842 66.5516 106.105C54.9551 99.1456 48.2575 92.4847 44.4213 87.4132C43.5683 86.2835 43.773 84.6794 44.8795 83.7991C45.1427 83.5888 45.3621 83.4079 45.4839 83.3003C48.472 80.6496 51.8891 76.5562 57.1145 70.2915C61.5455 64.9804 66.9952 58.4515 74.2192 50.343C75.2819 49.1497 77.0464 48.9003 78.3918 49.7513C82.6814 52.4557 92.1136 58.3146 99.8543 62.4373C116.949 71.5386 130.257 76.7861 138.246 80.1068C141.273 81.3636 142.224 84.6158 140.357 87.3154L140.362 87.3105ZM183.121 96.2601C168.375 105.547 154.234 114.184 145.797 119.035C143.028 120.63 141.663 117.006 143.666 113.387C149.258 103.263 157.417 87.6039 169.799 78.1016C169.799 78.1016 175.443 73.617 183.126 68.5211V96.2601H183.121ZM206.655 81.1876C201.459 84.5816 195.96 88.1174 190.433 91.6337V63.9093C193.401 62.139 196.487 60.4322 199.538 58.9552C223.886 47.1593 230.837 60.1583 230.837 60.1583C230.837 60.1583 222.395 70.9126 206.65 81.1924L206.655 81.1876ZM231.803 37.6717L181.395 8.47039C181.395 8.47039 186.577 9.30178 192.563 11.5025C199.533 14.0603 208.108 18.4128 218.807 24.5455C225.495 28.3797 233.772 35.0553 237.194 38.0287C235.639 37.8673 231.807 37.6668 231.807 37.6668L231.803 37.6717Z'
        fill='#4D2022'
      />
    </svg>
  </div>
);

const Benefit = ({ Icon, title }: BenefitProps) => (
  <div className='flex h-14 min-w-0 flex-1 flex-col items-center rounded-lg bg-white/10 p-2 text-center backdrop-blur-sm'>
    <div className='mb-1 shrink-0 text-white/90'>
      <Icon />
    </div>
    <p className='text-xs font-medium leading-tight text-white/90'>{title}</p>
  </div>
);

const PlusBanner = ({ isMobile }: PlusBannerProps) => {
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [activeSet, setActiveSet] = useState<number>(0);
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1200,
  );

  // 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 작은 화면 여부 (description 숨김용)
  const isSmallScreen = windowWidth < 768;

  const benefitSets = [
    [
      { Icon: NoLinesGraphic, title: 'No Lines' },
      { Icon: InstantAccessGraphic, title: 'Instant Access' },
      { Icon: SupportGraphic, title: '24/7 Support' },
    ],
    [
      { Icon: PassportGraphic, title: "Int'l License OK" },
      { Icon: EnglishSupportGraphic, title: 'EN Support' },
      { Icon: PricingGraphic, title: 'Clear Pricing' },
    ],
  ];

  useEffect(() => {
    if (!isVisible || isMobile) return;

    const interval = setInterval(() => {
      setActiveSet((prevSet: number) => (prevSet + 1) % benefitSets.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isVisible, isMobile, benefitSets.length]);

  if (!isVisible) return null;

  return (
    <div
      className='relative w-full overflow-hidden text-white'
      style={{
        background:
          'linear-gradient(135deg, #FF6D6F 0%, #FF8F90 50%, #FFB1B2 100%)',
        padding: isSmallScreen ? '8px 12px' : isMobile ? '16px 20px' : '16px 0',
        minWidth: isMobile ? '360px' : '1200px',
      }}
    >
      {/* Background Pattern */}
      <div className='absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/5 to-transparent'></div>

      {/* Car Silhouette  */}
      <CarSilhouette />

      <div
        className='relative z-10 mx-auto p-0'
        style={{
          width: isMobile ? '100%' : '1200px',
        }}
      >
        {/* Close Button - Top right corner */}
        <button
          onClick={() => setIsVisible(false)}
          className='absolute right-0 top-0 flex items-center justify-center rounded-full text-white/70 transition-all duration-200 hover:bg-white/20 hover:text-white'
          style={{
            width: isSmallScreen ? '20px' : '36px',
            height: isSmallScreen ? '20px' : '36px',
          }}
          aria-label='Close banner'
        >
          <CloseIcon />
        </button>

        {/* Main Layout - Desktop design for all */}
        <div
          className='flex items-center justify-between'
          style={{
            flexDirection: 'row',
            gap: isSmallScreen ? '16px' : '24px',
          }}
        >
          {/* Main Content */}
          <div className='min-w-0 flex-1 text-left'>
            <div className='mb-1 flex h-full items-center justify-start gap-2'>
              {/* Lightning Icon - Hidden on small screens */}
              {!isSmallScreen && (
                <div className='flex size-6 shrink-0 items-center justify-center rounded-full bg-white/20'>
                  <svg
                    className='size-4 text-yellow-300'
                    fill='currentColor'
                    viewBox='0 0 20 20'
                  >
                    <path
                      fillRule='evenodd'
                      d='M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z'
                      clipRule='evenodd'
                    />
                  </svg>
                </div>
              )}
              <h3
                className='font-bold text-white'
                style={{ fontSize: isSmallScreen ? '16px' : '20px' }}
              >
                Stop Waiting, Start Driving!
              </h3>
            </div>
            {/* Description - Hidden on small screens */}
            {!isSmallScreen && (
              <p className='text-white/90' style={{ fontSize: '14px' }}>
                International license accepted • No hidden fees • Instant
                booking
              </p>
            )}
          </div>

          {/* Benefits Section - Hidden on mobile */}
          {!isMobile && (
            <div className='shrink-0'>
              <div
                className='relative'
                style={{
                  height: '56px',
                  width: '320px',
                  maxWidth: '320px',
                }}
              >
                {benefitSets.map((set, setIndex) => (
                  <div
                    key={setIndex}
                    className={`absolute inset-0 flex items-center justify-center transition-all duration-700 ease-in-out ${
                      activeSet === setIndex
                        ? 'translate-y-0 opacity-100'
                        : 'translate-y-2 opacity-0'
                    }`}
                    style={{ gap: '12px' }}
                  >
                    {set.map((benefit) => (
                      <div
                        key={benefit.title}
                        className='flex min-w-0 flex-1 flex-col items-center justify-center rounded-lg bg-white/10 text-center backdrop-blur-sm'
                        style={{
                          padding: '8px',
                          height: '56px',
                          minWidth: '90px',
                        }}
                      >
                        <div className='mb-1 shrink-0 text-white/90'>
                          <benefit.Icon />
                        </div>
                        <p
                          className='font-medium leading-tight text-white/90'
                          style={{ fontSize: '12px' }}
                        >
                          {benefit.title}
                        </p>
                      </div>
                    ))}
                  </div>
                ))}

                {/* Progress Dots */}
                <div
                  className='absolute left-1/2 flex -translate-x-1/2'
                  style={{
                    bottom: '-4px',
                    gap: '6px',
                  }}
                >
                  {benefitSets.map((_, index) => (
                    <div
                      key={index}
                      className={`rounded-full transition-all duration-300 ${
                        activeSet === index ? 'bg-white' : 'bg-white/40'
                      }`}
                      style={{
                        width: '8px',
                        height: '8px',
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Action Button */}
          <div
            className='shrink-0'
            style={{
              marginRight: isSmallScreen ? '20px' : '40px',
            }}
          >
            <a
              href='https://plus.orcar.ai'
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex items-center rounded-full bg-white font-bold shadow-lg transition-all duration-200 hover:scale-105 hover:shadow-xl'
              style={{
                padding: isSmallScreen ? '10px 20px' : '12px 24px',
                fontSize: isSmallScreen ? '14px' : '16px',
                color: '#FF6D6F',
              }}
            >
              <span>Book Now</span>
              {!isSmallScreen && (
                <svg
                  className='ml-2 size-4'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M13 7l5 5m0 0l-5 5m5-5H6'
                  />
                </svg>
              )}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlusBanner;
